import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import * as Yup from 'yup';

import CardSelectorView from './CardSelectorView';
import FmcForm from 'components/FmcForm';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import { selectTenant } from 'state/tenant';
import {
  CreditApplication,
  selectCreditApplication,
  mergeApplication,
} from 'state/creditApplication';
import useQueryParameters from 'hooks/useQueryParameters';

type PropTypes = RouteComponentProps;
export type FormValues = Pick<CreditApplication, 'selectedCardKey'>;

const schema = Yup.object().shape({
  selectedCardKey: Yup.string()
    .nullable()
    .required(),
});

const CardSelector: React.FC<PropTypes> = props => {
  const application = useSelector(selectCreditApplication);
  const { defaultSelectedCardKey } = useQueryParameters();
  const { push } = useNavigationHelpers();
  const { pathAfter } = useWorkflow();
  const dispatch = useDispatch();

  const { match } = props;

  const nextPath =
    match.path === '/card-selector-fallback'
      ? pathAfter(Step.SECOND_CARD_SELECTOR)
      : pathAfter(Step.CARD_SELECTOR);

  const {
    enabledFeatures: { cardSelector },
  } = useSelector(selectTenant);

  if (!cardSelector) {
    // Should never happens
    throw new Error('Invalid tenant');
  }

  const initialValues: FormValues = {
    selectedCardKey:
      application.selectedCardKey || defaultSelectedCardKey || '',
  };

  const onSubmit = useCallback(
    (values: FormValues) => {
      dispatch(mergeApplication(values));
      push(nextPath);
    },
    [dispatch, nextPath, push],
  );

  return (
    <FmcForm
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CardSelectorView cardSelector={cardSelector} {...formProps} />
      )}
    </FmcForm>
  );
};

export default CardSelector;
